/** @file viewportRuntime: one mounted viewport (scene, puppet, input, marks, quads, gizmo, HUD). */
import type { Engine } from '@babylonjs/core/Engines/engine.js';
import type { LoadedBundle } from '@zilo/pose3d-runtime-babylon/bundle/loadedBundle';
import type { EditorStore } from '../../state/editorTypes';
import type { ArtDeps, FitMode, MisfitImage } from '../art/artTypes';
import { cameraControl, type CameraControlHandle, type CameraFrame } from './cameraControl';
import { createViewportScene } from './createViewportScene';
import { cssToTarget } from './cssToTarget';
import { dropInput } from './dropInput';
import { gizmoFrameOf } from './gizmoFrameOf';
import { groundOf } from './groundOf';
import { hiddenFor } from './hiddenFor';
import { labelsOf } from './labelsOf';
import { marksOf } from './marksOf';
import { onionSkin, type OnionSkin } from './onionSkin';
import { outlineOf } from './outlineOf';
import { pointerInput } from './pointerInput';
import { puppetSync, type PuppetSync } from './puppetSync';
import { quadHandlesOf } from './quadHandlesOf';
import { quadsOf } from './quadsOf';
import { syncQuadOverride } from './syncQuadOverride';
import { viewportCommands } from './viewportCommands';
import type { ViewportCtx, ViewportMode } from './viewportCtx';
import { viewportHud, type ViewportHud } from './viewportHud';
import { viewportLabels, type ViewportLabels } from './viewportLabels';
import { windowEvents } from './windowEvents';

/** The DOM a viewport draws into: the box (input target) and the one Babylon canvas. */
export type ViewportElements = { container: HTMLElement; canvas: HTMLCanvasElement };

/**
 * Mode, an engine factory (tests: NullEngine; default the runtime's `createEngine`), the HUD listener (10 Hz), the
 * upload call drops, pastes and refits use (none: they toast) and the fit prompt (none: every misfit is cancelled).
 */
export type ViewportRuntimeOptions = {
  mode: ViewportMode; engine?: () => Engine; onHud?: (hud: ViewportHud) => void;
  deps?: ArtDeps; askFit?: (misfits: MisfitImage[]) => Promise<FitMode | null>;
};

/** The running viewport: its command handlers (for the key table), `step()` (one frame now, for tests), one teardown. */
export type ViewportRuntime = { handlers: Record<string, () => void>; ctx: ViewportCtx; step(): void; dispose(): void };

/** The parts that exist once the first bundle gave a profile. */
type Live = { camera: CameraControlHandle; sync: PuppetSync; onion: OnionSkin };

/** Chooses `idle` (else the first clip) when the playhead names no clip of the bundle. */
function defaultClip(store: EditorStore, bundle: LoadedBundle): void {
  const s = store.getState();
  const names = Object.keys(bundle.clips);
  if (s.playhead.clip !== null && names.includes(s.playhead.clip)) return;
  const clip = names.includes('idle') ? 'idle' : names[0];
  if (clip !== undefined) s.setPlayhead({ clip, frame: 0 });
}

/** The default view of a bundle's puppet: its bounding centre and rest height (`fitZoomFor` frames it). */
const frameOf = (bundle: LoadedBundle): CameraFrame => ({ home: [0, 0, bundle.puppet.bounds[2] / 2], height: bundle.puppet.bounds[2] });

/** Creates the scene, camera control, puppet sync and onion skin for `bundle`'s profile. */
function start(ctx: ViewportCtx, els: ViewportElements, opts: ViewportRuntimeOptions, bundle: LoadedBundle): Live {
  const s = ctx.store.getState();
  const vs = createViewportScene(els.canvas, bundle.profile, { ...(opts.engine === undefined ? {} : { engine: opts.engine() }), camera: { yawDeg: s.camera.yawDeg, pitchDeg: s.camera.pitchDeg } });
  const camera = cameraControl(vs, els.canvas, els.container, bundle.profile, frameOf(bundle));
  Object.assign(ctx, { scene: vs, profile: bundle.profile, camera });
  const sync = puppetSync(vs.scene, (frame) => { ctx.store.getState().setPlayhead({ frame }); });
  ctx.puppet = () => sync.current();
  return { camera, sync, onion: onionSkin(vs.scene) };
}

/** A landed edit keeps its pose override until the rebuilt preview is the one being drawn (no snap back between). */
function releaseHeldOverride(ctx: ViewportCtx, l: Live, s: ReturnType<EditorStore['getState']>): void {
  if (ctx.holdOverrideUntil === null) return;
  const p = s.preview;
  const drawn = p.revision >= ctx.holdOverrideUntil && !p.loading && l.sync.current()?.source === p.bundle;
  if (!drawn && p.error === null) return;
  ctx.holdOverrideUntil = null;
  s.setPoseOverride(null);
}

/** A landed quad drag keeps drawing its result until the rebuilt preview draws it (or the rebuild failed). */
function releaseHeldQuad(ctx: ViewportCtx, l: Live, s: ReturnType<EditorStore['getState']>): void {
  const until = ctx.quad.holdUntil;
  if (until === null) return;
  const p = s.preview;
  if (p.error === null && (p.revision < until || p.loading || l.sync.current()?.source !== p.bundle)) return;
  ctx.quad.holdUntil = null;
  ctx.quad.edit = null;
}

/** One rendered frame: camera, grid, puppet, onion, marks / outline / quads / gizmo, one `scene.render()`, labels, HUD. */
function drawFrame(ctx: ViewportCtx, l: Live, hud: { tick(): void }, labels: ViewportLabels): void {
  const vs = ctx.scene;
  if (vs === null) return;
  const s = ctx.store.getState();
  releaseHeldOverride(ctx, l, s);
  releaseHeldQuad(ctx, l, s);
  syncQuadOverride(ctx, l.sync.current()?.source ?? null);
  l.camera.sync(s.camera);
  vs.setGridVisible(s.camera.grid);
  l.sync.update(s, vs.engine.getDeltaTime() / 1000, vs.camera, hiddenFor(ctx));
  l.onion.update(l.sync.current(), s, vs.camera);
  vs.ground.update(groundOf(ctx));
  vs.marks.update(marksOf(ctx)); // joints off: only the selection's bones (marksOf)
  vs.outline.update(outlineOf(ctx));
  const px = cssToTarget(ctx) / vs.camera.view().pixelsPerUnit;
  vs.quads.update(quadsOf(ctx, px));
  vs.handles.update(quadHandlesOf(ctx, px));
  const gizmo = gizmoFrameOf(ctx);
  vs.gizmo.update(gizmo);
  vs.scene.render();
  labels.update(labelsOf(ctx, gizmo));
  hud.tick();
}

/**
 * Waits for `preview.bundle`, then renders every frame inside the engine's render loop: store camera -> orbit
 * camera, grid toggle, the puppet (`puppetSync`, isolate and tree-eye hiding), onion ghosts, the joint marks, the part
 * quads and their handles and the gizmo (meshes
 * updated in place), then one `scene.render()` that draws them all in the same pass, then the HUD. No
 * `requestAnimationFrame` of its own, no React state per frame; the canvas is resized only on a real size change.
 */
export function viewportRuntime(els: ViewportElements, store: EditorStore, opts: ViewportRuntimeOptions): ViewportRuntime {
  const ctx: ViewportCtx = {
    store, mode: opts.mode, canvas: els.canvas, container: els.container, scene: null, profile: null,
    puppet: () => null, camera: null, isolate: false, hover: null, hoverNode: null, edit: null, holdOverrideUntil: null,
    quad: { hover: -1, handle: null, drop: -1, pointerIn: false, edit: null, holdUntil: null, lastClick: null },
    deps: opts.deps ?? null, askFit: opts.askFit ?? (() => Promise.resolve(null)),
  };
  const handlers = viewportCommands(ctx);
  const detach = pointerInput(ctx, handlers);
  const detachDrop = dropInput(ctx);
  const detachWindow = windowEvents(ctx);
  const hud = viewportHud(ctx, opts.onHud);
  const labels = viewportLabels(els.container);
  let live: Live | null = null;
  const frame = (l: Live): void => { drawFrame(ctx, l, hud, labels); };
  let opened: string | null = null;
  const onState = (bundle: LoadedBundle | null): void => {
    if (bundle === null) return;
    const id = bundle.bundle.manifest.id;
    if (live === null) {
      const l = start(ctx, els, opts, bundle);
      live = l;
      ctx.scene?.engine.runRenderLoop(() => { frame(l); });
    } else if (id !== opened) live.camera.reframe(frameOf(bundle));
    opened = id;
    defaultClip(store, bundle);
  };
  const unsubscribe = store.subscribe((s) => { onState(s.preview.bundle); });
  onState(store.getState().preview.bundle);
  return {
    handlers, ctx,
    step: () => { if (live !== null) frame(live); },
    dispose: () => {
      unsubscribe();
      detach();
      detachDrop();
      detachWindow();
      labels.dispose();
      live?.onion.dispose();
      live?.sync.dispose();
      live?.camera.dispose();
      ctx.scene?.dispose();
      live = null;
    },
  };
}
